/**
 * WorkflowPage — Workflow Automation Engine UI
 * Tabs: Dashboard | Rules | Execution Logs
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Button, Badge, Table, Modal, Form,
    Spinner, Alert, ProgressBar, OverlayTrigger, Tooltip,
    Accordion
} from 'react-bootstrap';
import {
    Zap, Plus, Play, Pause, Trash2, Edit2, BarChart2,
    CheckCircle, XCircle, AlertCircle, Clock, RefreshCw,
    ChevronRight, Eye, ToggleLeft, ToggleRight, Beaker
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-IN', {
        day:'numeric', month:'short', hour:'2-digit', minute:'2-digit',
    });
};

const STATUS_CONFIG = {
    success: { bg:'success', label:'Success',  icon:<CheckCircle size={13}/> },
    error:   { bg:'danger',  label:'Error',    icon:<XCircle size={13}/>     },
    partial: { bg:'warning', label:'Partial',  icon:<AlertCircle size={13}/>  },
};

const EVENT_CATEGORY_COLOURS = {
    Leave:'primary', Attendance:'warning', Employee:'info',
    Expense:'success', Helpdesk:'danger', Onboarding:'indigo',
    Offboarding:'secondary', Performance:'pink', Documents:'teal',
};
const catColour = (cat) => EVENT_CATEGORY_COLOURS[cat] || 'secondary';

// ════════════════════════════════════════════════════════════════════════════
const WorkflowPage = () => {
    const [tab,      setTab]      = useState('dashboard');
    const [catalogue,setCatalogue]= useState(null);

    useEffect(() => {
        axiosInstance.get('/workflows/events').then(r => setCatalogue(r.data.data)).catch(()=>{});
    }, []);

    const tabs = [
        { key:'dashboard', label:'Dashboard',       icon:BarChart2 },
        { key:'rules',     label:'Automation Rules', icon:Zap       },
        { key:'logs',      label:'Execution Logs',   icon:Clock     },
    ];

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Zap size={22} className="me-2 text-warning"/>
                        Workflow Automation
                    </h4>
                    <p className="text-muted small mb-0">
                        Trigger-action rules · Background jobs · Event log
                    </p>
                </div>
            </div>
            <div className="d-flex gap-1 mb-4">
                {tabs.map(t => {
                    const Icon = t.icon;
                    return (
                        <Button key={t.key} size="sm"
                            variant={tab===t.key ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab(t.key)}>
                            <Icon size={13} className="me-1"/>{t.label}
                        </Button>
                    );
                })}
            </div>

            {tab==='dashboard' && <DashboardTab/>}
            {tab==='rules'     && <RulesTab catalogue={catalogue}/>}
            {tab==='logs'      && <LogsTab/>}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ════════════════════════════════════════════════════════════════════════════
const DashboardTab = () => {
    const [stats,   setStats]   = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        axiosInstance.get('/workflows/stats')
            .then(r => setStats(r.data.data))
            .catch(() => toast.error('Failed to load stats'))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="warning"/></div>;
    if (!stats)  return null;

    const { summary = {}, log_stats = {}, top_rules = [], recent_errors = [], daily_runs = [] } = stats;

    const successRate = log_stats.total_logs
        ? Math.round((log_stats.success / log_stats.total_logs) * 100) : 100;

    return (
        <>
            {/* KPI row */}
            <Row className="g-3 mb-4">
                {[
                    { label:'Active Rules',      val:summary.active_rules||0,      color:'#f59e0b' },
                    { label:'Total Executions',  val:summary.total_executions||0,  color:'#4f46e5' },
                    { label:'Success Rate (30d)', val:`${successRate}%`,            color:'#059669' },
                    { label:'Avg Duration',      val:`${log_stats.avg_duration_ms||0}ms`, color:'#0891b2' },
                ].map((k, i) => (
                    <Col key={i} xs={6} md={3}>
                        <Card className="border-0 shadow-sm p-4 text-center h-100">
                            <div style={{ fontSize:28, fontWeight:800, color:k.color }}>{k.val}</div>
                            <div className="text-muted small">{k.label}</div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="g-4 mb-4">
                {/* Daily runs chart */}
                <Col lg={8}>
                    <Card className="border-0 shadow-sm p-4 h-100">
                        <div className="fw-bold mb-3">Daily Executions (last 14 days)</div>
                        {daily_runs.length === 0 ? (
                            <div className="text-center text-muted py-4">No executions yet.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={daily_runs}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                    <XAxis dataKey="date" tick={{ fontSize:10 }}
                                        tickFormatter={d => d?.slice(5)}/>
                                    <YAxis allowDecimals={false} tick={{ fontSize:11 }}/>
                                    <RTooltip/>
                                    <Bar dataKey="runs"   name="Runs"   fill="#4f46e5" radius={[3,3,0,0]}/>
                                    <Bar dataKey="errors" name="Errors" fill="#dc2626" radius={[3,3,0,0]}/>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </Col>

                {/* Top rules */}
                <Col lg={4}>
                    <Card className="border-0 shadow-sm p-4 h-100">
                        <div className="fw-bold mb-3">Most Triggered Rules</div>
                        {top_rules.length === 0 ? (
                            <div className="text-muted small">No executions yet.</div>
                        ) : top_rules.map((r, i) => (
                            <div key={r.id} className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <div className="small fw-medium">{r.name}</div>
                                    <div className="text-muted" style={{ fontSize:10 }}>
                                        {r.trigger_event}
                                    </div>
                                </div>
                                <Badge bg="light" text="dark" className="fw-bold">
                                    {r.run_count}×
                                </Badge>
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>

            {/* Recent errors */}
            {recent_errors.length > 0 && (
                <Card className="border-0 shadow-sm border-start border-danger border-3">
                    <Card.Header className="bg-white fw-bold border-0 pt-3 text-danger">
                        <AlertCircle size={16} className="me-2"/>Recent Errors
                    </Card.Header>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize:10 }}>
                                <th className="ps-4">Rule</th>
                                <th>Event</th>
                                <th>Error</th>
                                <th className="pe-4">Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent_errors.map(e => (
                                <tr key={e.id}>
                                    <td className="ps-4 fw-medium">{e.rule_name}</td>
                                    <td><Badge bg="light" text="dark" style={{ fontSize:10 }}>{e.trigger_event}</Badge></td>
                                    <td className="text-danger small">{e.error_message}</td>
                                    <td className="pe-4 text-muted">{fmtDate(e.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// RULES TAB
// ════════════════════════════════════════════════════════════════════════════
const RulesTab = ({ catalogue }) => {
    const [rules,   setRules]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null=list, {}=new, {id}=edit
    const [testing, setTesting] = useState(null);

    const load = () => {
        setLoading(true);
        axiosInstance.get('/workflows').then(r => setRules(r.data.data || []))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const toggle = async (rule) => {
        try {
            const res = await axiosInstance.patch(`/workflows/${rule.id}/toggle`);
            toast.success(res.data.message);
            load();
        } catch { toast.error('Failed'); }
    };

    const del = async (rule) => {
        if (!window.confirm(`Delete "${rule.name}"? This cannot be undone.`)) return;
        try {
            await axiosInstance.delete(`/workflows/${rule.id}`);
            toast.success('Rule deleted');
            load();
        } catch { toast.error('Failed'); }
    };

    if (editing !== null) return (
        <RuleEditor
            rule={editing}
            catalogue={catalogue}
            onBack={() => setEditing(null)}
            onDone={() => { setEditing(null); load(); }}
        />
    );

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="warning"/></div>;

    return (
        <>
            <div className="d-flex justify-content-between mb-3">
                <div className="text-muted small">
                    {rules.length} rule{rules.length !== 1 ? 's' : ''}
                </div>
                <Button size="sm" variant="warning" onClick={() => setEditing({})}>
                    <Plus size={14} className="me-1"/>New Rule
                </Button>
            </div>

            {rules.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <Zap size={40} className="mx-auto mb-3 opacity-30"/>
                    <p>No automation rules yet. Create your first rule to automate repetitive tasks.</p>
                    <div className="d-flex gap-2 justify-content-center flex-wrap mt-2">
                        {[
                            'Auto-notify HR when employee submits leave',
                            'Send birthday wishes automatically',
                            'Alert on overdue helpdesk tickets',
                        ].map((ex, i) => (
                            <Badge key={i} bg="light" text="dark" className="fw-normal p-2">
                                {ex}
                            </Badge>
                        ))}
                    </div>
                    <Button variant="warning" size="sm" className="mx-auto mt-3"
                        onClick={() => setEditing({})}>
                        <Plus size={14} className="me-1"/>Create First Rule
                    </Button>
                </Card>
            ) : (
                <Row className="g-3">
                    {rules.map(rule => (
                        <Col key={rule.id} xs={12} md={6} lg={4}>
                            <Card className="border-0 shadow-sm h-100 p-4"
                                style={{ borderLeft:`3px solid ${rule.is_active ? '#f59e0b' : '#e5e7eb'}`,
                                    opacity: rule.is_active ? 1 : 0.7 }}>
                                <div className="d-flex justify-content-between mb-2">
                                    <Badge bg={rule.is_active ? 'warning' : 'secondary'}>
                                        {rule.is_active ? '⚡ Active' : '⏸ Paused'}
                                    </Badge>
                                    <Badge bg="light" text="dark" style={{ fontSize:10 }}>
                                        {rule.run_count || 0} runs
                                    </Badge>
                                </div>
                                <h6 className="fw-bold mb-2">{rule.name}</h6>
                                {rule.description && (
                                    <p className="text-muted small mb-3">{rule.description}</p>
                                )}
                                <div className="mb-3">
                                    <div className="text-muted" style={{ fontSize:11 }}>WHEN</div>
                                    <Badge bg="light" text="dark" className="fw-normal">
                                        {rule.trigger_event}
                                    </Badge>
                                    {(rule.trigger_filters||[]).length > 0 && (
                                        <div className="text-muted mt-1" style={{ fontSize:10 }}>
                                            + {rule.trigger_filters.length} filter(s)
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <div className="text-muted" style={{ fontSize:11 }}>THEN</div>
                                    <div className="d-flex gap-1 flex-wrap">
                                        {(rule.actions||[]).map((a, i) => (
                                            <Badge key={i} bg="primary" style={{ fontSize:10 }}>
                                                {a.type?.replace(/_/g,' ')}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                {rule.last_run_at && (
                                    <div className="text-muted mb-3" style={{ fontSize:11 }}>
                                        <Clock size={10} className="me-1"/>
                                        Last run {fmtDate(rule.last_run_at)}
                                    </div>
                                )}
                                {rule.logs_30d > 0 && (
                                    <div className="text-muted mb-3" style={{ fontSize:11 }}>
                                        {rule.logs_30d} execution{rule.logs_30d!==1?'s':''} in last 30 days
                                    </div>
                                )}
                                <div className="d-flex gap-1 mt-auto">
                                    <Button size="sm" variant="outline-secondary" className="py-0"
                                        onClick={() => setEditing(rule)}>
                                        <Edit2 size={12}/>
                                    </Button>
                                    <Button size="sm" variant="outline-info" className="py-0"
                                        title="Test rule"
                                        onClick={() => setTesting(rule)}>
                                        <Beaker size={12}/>
                                    </Button>
                                    <Button size="sm"
                                        variant={rule.is_active ? 'outline-secondary' : 'outline-warning'}
                                        className="py-0" title={rule.is_active ? 'Pause' : 'Enable'}
                                        onClick={() => toggle(rule)}>
                                        {rule.is_active
                                            ? <Pause size={12}/>
                                            : <Play size={12}/>}
                                    </Button>
                                    <Button size="sm" variant="outline-danger" className="py-0"
                                        onClick={() => del(rule)}>
                                        <Trash2 size={12}/>
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {testing && (
                <TestRuleModal rule={testing} onClose={() => { setTesting(null); load(); }}/>
            )}
        </>
    );
};

// ── Rule Editor ───────────────────────────────────────────────────────────────
const RuleEditor = ({ rule, catalogue, onBack, onDone }) => {
    const isNew = !rule.id;
    const [form, setForm] = useState({
        name:            rule.name            || '',
        description:     rule.description     || '',
        trigger_event:   rule.trigger_event   || '',
        trigger_filters: rule.trigger_filters || [],
        actions:         rule.actions         || [],
        is_active:       rule.is_active !== false,
    });
    const [saving, setSaving] = useState(false);

    const events  = catalogue?.trigger_events  || [];
    const aTypes  = catalogue?.action_types    || [];
    const ops     = catalogue?.filter_operators|| [];

    const selectedEvent = events.find(e => e.event === form.trigger_event);

    const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

    // Filters
    const addFilter = () => setField('trigger_filters', [
        ...form.trigger_filters, { field:'', operator:'eq', value:'' }
    ]);
    const updateFilter = (i, k, v) => {
        const f = [...form.trigger_filters];
        f[i] = { ...f[i], [k]: v };
        setField('trigger_filters', f);
    };
    const removeFilter = (i) => setField('trigger_filters',
        form.trigger_filters.filter((_, j) => j !== i));

    // Actions
    const addAction = () => setField('actions', [
        ...form.actions, { type:'send_notification', config:{}, delay_seconds:0 }
    ]);
    const updateAction = (i, k, v) => {
        const a = [...form.actions];
        a[i] = { ...a[i], [k]: v };
        setField('actions', a);
    };
    const updateActionConfig = (i, k, v) => {
        const a = [...form.actions];
        a[i] = { ...a[i], config: { ...(a[i].config||{}), [k]: v } };
        setField('actions', a);
    };
    const removeAction = (i) => setField('actions', form.actions.filter((_, j) => j !== i));

    const save = async () => {
        if (!form.name.trim())    return toast.error('Rule name is required');
        if (!form.trigger_event)  return toast.error('Select a trigger event');
        if (!form.actions.length) return toast.error('Add at least one action');
        setSaving(true);
        try {
            if (isNew) {
                await axiosInstance.post('/workflows', form);
                toast.success('Rule created');
            } else {
                await axiosInstance.put(`/workflows/${rule.id}`, form);
                toast.success('Rule updated');
            }
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    return (
        <div>
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>
                ← Rules
            </Button>
            <h5 className="fw-bold mb-4">{isNew ? 'Create New Rule' : `Edit: ${rule.name}`}</h5>

            {/* Basic info */}
            <Card className="border-0 shadow-sm mb-4 p-4">
                <Row className="g-3">
                    <Col xs={8}>
                        <Form.Label className="small fw-bold">Rule Name *</Form.Label>
                        <Form.Control placeholder="e.g. Notify HR on leave submission"
                            value={form.name} onChange={e => setField('name', e.target.value)}/>
                    </Col>
                    <Col xs={4} className="d-flex align-items-end">
                        <Form.Check type="switch" label="Active" className="mb-2"
                            checked={form.is_active}
                            onChange={e => setField('is_active', e.target.checked)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Description</Form.Label>
                        <Form.Control placeholder="Optional — what does this rule do?"
                            value={form.description}
                            onChange={e => setField('description', e.target.value)}/>
                    </Col>
                </Row>
            </Card>

            {/* WHEN — trigger */}
            <Card className="border-0 shadow-sm mb-4 p-4">
                <div className="fw-bold mb-3">
                    <span className="badge bg-warning text-dark me-2">WHEN</span>
                    Select trigger event
                </div>
                <Form.Select value={form.trigger_event}
                    onChange={e => setField('trigger_event', e.target.value)}>
                    <option value="">— Choose an event —</option>
                    {Object.entries(
                        events.reduce((acc, e) => {
                            (acc[e.category] = acc[e.category]||[]).push(e);
                            return acc;
                        }, {})
                    ).map(([cat, evts]) => (
                        <optgroup key={cat} label={cat}>
                            {evts.map(e => (
                                <option key={e.event} value={e.event}>{e.label}</option>
                            ))}
                        </optgroup>
                    ))}
                </Form.Select>

                {selectedEvent && (
                    <Alert variant="light" className="mt-3 mb-0 small py-2">
                        Available payload fields:&nbsp;
                        {selectedEvent.payload_fields.map(f => (
                            <code key={f} className="me-1 bg-light px-1">{`{{${f}}}`}</code>
                        ))}
                    </Alert>
                )}
            </Card>

            {/* Filters */}
            {form.trigger_event && (
                <Card className="border-0 shadow-sm mb-4 p-4">
                    <div className="d-flex justify-content-between mb-3">
                        <div className="fw-bold">
                            <span className="badge bg-secondary me-2">IF</span>
                            Conditions (optional — all must match)
                        </div>
                        <Button size="sm" variant="outline-secondary" onClick={addFilter}>
                            <Plus size={12} className="me-1"/>Add Condition
                        </Button>
                    </div>
                    {form.trigger_filters.length === 0 && (
                        <div className="text-muted small">
                            No conditions — rule applies to every occurrence of the event.
                        </div>
                    )}
                    {form.trigger_filters.map((f, i) => (
                        <Row key={i} className="g-2 mb-2 align-items-center">
                            <Col xs={4}>
                                <Form.Control size="sm" placeholder="Field (e.g. leave_type)"
                                    value={f.field}
                                    onChange={e => updateFilter(i, 'field', e.target.value)}/>
                            </Col>
                            <Col xs={3}>
                                <Form.Select size="sm" value={f.operator}
                                    onChange={e => updateFilter(i, 'operator', e.target.value)}>
                                    {ops.map(o => <option key={o.op} value={o.op}>{o.label}</option>)}
                                </Form.Select>
                            </Col>
                            <Col xs={4}>
                                <Form.Control size="sm" placeholder="Value"
                                    value={f.value}
                                    onChange={e => updateFilter(i, 'value', e.target.value)}/>
                            </Col>
                            <Col xs={1}>
                                <Button size="sm" variant="outline-danger" className="py-0"
                                    onClick={() => removeFilter(i)}>✕</Button>
                            </Col>
                        </Row>
                    ))}
                </Card>
            )}

            {/* Actions */}
            {form.trigger_event && (
                <Card className="border-0 shadow-sm mb-4 p-4">
                    <div className="d-flex justify-content-between mb-3">
                        <div className="fw-bold">
                            <span className="badge bg-primary me-2">THEN</span>
                            Actions (executed in order)
                        </div>
                        <Button size="sm" variant="outline-primary" onClick={addAction}>
                            <Plus size={12} className="me-1"/>Add Action
                        </Button>
                    </div>
                    {form.actions.length === 0 && (
                        <div className="text-muted small">Add at least one action.</div>
                    )}
                    {form.actions.map((action, i) => {
                        const meta = aTypes.find(t => t.type === action.type) || {};
                        return (
                            <Card key={i} className="border mb-3 p-3"
                                style={{ background:'#fafafa' }}>
                                <Row className="g-2 mb-2 align-items-center">
                                    <Col xs={4}>
                                        <Form.Label className="small fw-bold mb-1">Action Type</Form.Label>
                                        <Form.Select size="sm" value={action.type}
                                            onChange={e => updateAction(i, 'type', e.target.value)}>
                                            {aTypes.map(t => (
                                                <option key={t.type} value={t.type}>{t.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col xs={3}>
                                        <Form.Label className="small fw-bold mb-1">
                                            Delay (seconds)
                                        </Form.Label>
                                        <Form.Control size="sm" type="number" min={0} max={30}
                                            value={action.delay_seconds || 0}
                                            onChange={e => updateAction(i, 'delay_seconds', parseInt(e.target.value)||0)}/>
                                    </Col>
                                    <Col xs={5} className="d-flex align-items-end justify-content-end">
                                        <Button size="sm" variant="outline-danger" className="py-0"
                                            onClick={() => removeAction(i)}>
                                            <Trash2 size={12} className="me-1"/>Remove
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Action-specific config fields */}
                                <ActionConfigFields
                                    action={action} meta={meta} idx={i}
                                    payloadFields={selectedEvent?.payload_fields || []}
                                    onUpdate={updateActionConfig}
                                />
                            </Card>
                        );
                    })}
                </Card>
            )}

            <div className="d-flex gap-2">
                <Button variant="light" onClick={onBack}>Cancel</Button>
                <Button variant="warning" onClick={save} disabled={saving}>
                    {saving ? <Spinner size="sm"/> : (isNew ? '⚡ Create Rule' : '💾 Save Changes')}
                </Button>
            </div>
        </div>
    );
};

// ── Action Config Fields ──────────────────────────────────────────────────────
const ActionConfigFields = ({ action, meta, idx, payloadFields, onUpdate }) => {
    const c = action.config || {};
    const targets = meta.targets || ['employee','manager','hr','admin'];

    switch (action.type) {
        case 'send_notification':
            return (
                <Row className="g-2 mt-1">
                    <Col xs={4}>
                        <Form.Label className="small">Send To</Form.Label>
                        <Form.Select size="sm" value={c.target||'employee'}
                            onChange={e => onUpdate(idx, 'target', e.target.value)}>
                            {targets.map(t => <option key={t} value={t}>{t}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={8}>
                        <Form.Label className="small">Title</Form.Label>
                        <Form.Control size="sm" placeholder="e.g. Leave request from {{employee_name}}"
                            value={c.title||''} onChange={e => onUpdate(idx, 'title', e.target.value)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small">Message</Form.Label>
                        <Form.Control as="textarea" rows={2} size="sm"
                            placeholder="Message body — use {{field}} for dynamic values"
                            value={c.message||''} onChange={e => onUpdate(idx, 'message', e.target.value)}/>
                    </Col>
                </Row>
            );

        case 'send_email':
            return (
                <Row className="g-2 mt-1">
                    <Col xs={4}>
                        <Form.Label className="small">Send To</Form.Label>
                        <Form.Select size="sm" value={c.target||'employee'}
                            onChange={e => onUpdate(idx, 'target', e.target.value)}>
                            {targets.map(t => <option key={t} value={t}>{t}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={8}>
                        <Form.Label className="small">Subject</Form.Label>
                        <Form.Control size="sm" placeholder="Email subject"
                            value={c.subject||''} onChange={e => onUpdate(idx, 'subject', e.target.value)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small">Body (HTML supported)</Form.Label>
                        <Form.Control as="textarea" rows={3} size="sm"
                            placeholder="Email body — {{employee_name}}, {{leave_type}}, etc."
                            value={c.body||''} onChange={e => onUpdate(idx, 'body', e.target.value)}/>
                    </Col>
                </Row>
            );

        case 'webhook':
            return (
                <Row className="g-2 mt-1">
                    <Col xs={8}>
                        <Form.Label className="small">URL</Form.Label>
                        <Form.Control size="sm" type="url"
                            placeholder="https://hooks.slack.com/services/..."
                            value={c.url||''} onChange={e => onUpdate(idx, 'url', e.target.value)}/>
                    </Col>
                    <Col xs={4}>
                        <Form.Label className="small">Method</Form.Label>
                        <Form.Select size="sm" value={c.method||'POST'}
                            onChange={e => onUpdate(idx, 'method', e.target.value)}>
                            {['POST','PUT','GET'].map(m => <option key={m}>{m}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small">Body Template (JSON)</Form.Label>
                        <Form.Control as="textarea" rows={2} size="sm"
                            placeholder={'{"text":"{{employee_name}} submitted a leave request"}'}
                            value={c.body_template||''}
                            onChange={e => onUpdate(idx, 'body_template', e.target.value)}/>
                    </Col>
                </Row>
            );

        case 'create_task':
            return (
                <Row className="g-2 mt-1">
                    <Col xs={4}>
                        <Form.Label className="small">Priority</Form.Label>
                        <Form.Select size="sm" value={c.priority||'medium'}
                            onChange={e => onUpdate(idx, 'priority', e.target.value)}>
                            {['urgent','high','medium','low'].map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={8}>
                        <Form.Label className="small">Title</Form.Label>
                        <Form.Control size="sm" placeholder="Task title — {{employee_name}}"
                            value={c.title||''} onChange={e => onUpdate(idx, 'title', e.target.value)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small">Description</Form.Label>
                        <Form.Control as="textarea" rows={2} size="sm"
                            value={c.description||''} onChange={e => onUpdate(idx, 'description', e.target.value)}/>
                    </Col>
                </Row>
            );

        case 'add_announcement':
            return (
                <Row className="g-2 mt-1">
                    <Col xs={12}>
                        <Form.Label className="small">Title</Form.Label>
                        <Form.Control size="sm" placeholder="Announcement title"
                            value={c.title||''} onChange={e => onUpdate(idx, 'title', e.target.value)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small">Content</Form.Label>
                        <Form.Control as="textarea" rows={2} size="sm"
                            value={c.content||''} onChange={e => onUpdate(idx, 'content', e.target.value)}/>
                    </Col>
                </Row>
            );

        default:
            return null;
    }
};

// ── Test Rule Modal ───────────────────────────────────────────────────────────
const TestRuleModal = ({ rule, onClose }) => {
    const [running, setRunning]   = useState(false);
    const [results, setResults]   = useState(null);
    const [dryRun,  setDryRun]    = useState(true);
    const [payload, setPayload]   = useState(
        JSON.stringify({ employee_id: '', employee_name: 'Test Employee' }, null, 2)
    );

    const run = async () => {
        let parsed;
        try { parsed = JSON.parse(payload); }
        catch { toast.error('Invalid JSON payload'); return; }
        setRunning(true);
        try {
            const res = await axiosInstance.post(`/workflows/${rule.id}/test`, {
                sample_payload: parsed,
                dry_run: dryRun,
            });
            setResults(res.data.data);
        } catch (err) { toast.error(err.response?.data?.message || 'Test failed'); }
        finally { setRunning(false); }
    };

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <Beaker size={18} className="me-2 text-info"/>Test: {rule.name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="small py-2 mb-3">
                    Test fires the rule with sample data.
                    <strong> Dry run</strong> validates the rule structure without sending notifications or emails.
                </Alert>

                <Form.Check type="switch" label="Dry run (no actions executed)"
                    checked={dryRun} className="mb-3"
                    onChange={e => setDryRun(e.target.checked)}/>

                <Form.Label className="small fw-bold">Sample Payload (JSON)</Form.Label>
                <Form.Control as="textarea" rows={6}
                    style={{ fontFamily:'monospace', fontSize:12 }}
                    value={payload}
                    onChange={e => setPayload(e.target.value)}/>

                {results && (
                    <div className="mt-3">
                        <div className="fw-bold small mb-2">Results:</div>
                        {Array.isArray(results.results) ? (
                            results.results.map((r, i) => (
                                <div key={i} className="p-2 rounded mb-1"
                                    style={{ background: r.error ? '#fff1f2' : '#f0fdf4',
                                        fontSize:12 }}>
                                    <span className="fw-medium">{r.type}</span>
                                    {r.error
                                        ? <span className="text-danger ms-2">✗ {r.error}</span>
                                        : <span className="text-success ms-2">✓ OK</span>}
                                </div>
                            ))
                        ) : (
                            <pre style={{ fontSize:11 }}>{JSON.stringify(results, null, 2)}</pre>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Close</Button>
                <Button variant={dryRun ? 'info' : 'warning'} onClick={run} disabled={running}>
                    {running ? <Spinner size="sm"/> : (dryRun ? '🔍 Validate' : '⚡ Run Test')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// LOGS TAB
// ════════════════════════════════════════════════════════════════════════════
const LogsTab = () => {
    const [logs,     setLogs]    = useState([]);
    const [loading,  setLoading] = useState(true);
    const [detail,   setDetail]  = useState(null);
    const [statusF,  setStatusF] = useState('');

    const load = () => {
        setLoading(true);
        axiosInstance.get('/workflows/logs', { params: { status: statusF||undefined, limit:100 }})
            .then(r => setLogs(r.data.data || []))
            .catch(() => toast.error('Failed'))
            .finally(() => setLoading(false));
    };
    useEffect(load, [statusF]);

    const prune = async () => {
        if (!window.confirm('Delete logs older than 90 days?')) return;
        try {
            const r = await axiosInstance.delete('/workflows/logs/prune?days=90');
            toast.success(r.data.message);
            load();
        } catch { toast.error('Failed'); }
    };

    return (
        <>
            <div className="d-flex gap-2 mb-3 align-items-center">
                <Form.Select size="sm" style={{ width:160 }} value={statusF}
                    onChange={e => setStatusF(e.target.value)}>
                    <option value="">All statuses</option>
                    <option value="success">Success</option>
                    <option value="error">Error</option>
                    <option value="partial">Partial</option>
                </Form.Select>
                <Button size="sm" variant="outline-secondary" onClick={load}>
                    <RefreshCw size={13} className="me-1"/>Refresh
                </Button>
                <Button size="sm" variant="outline-danger" className="ms-auto" onClick={prune}>
                    Prune old logs
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-4"><Spinner animation="border"/></div>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize:10 }}>
                                <th className="ps-4">Rule</th>
                                <th>Event</th>
                                <th>Status</th>
                                <th>Duration</th>
                                <th>Error</th>
                                <th className="pe-4">Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-muted py-4">
                                    No execution logs yet.
                                </td></tr>
                            )}
                            {logs.map(log => {
                                const sc = STATUS_CONFIG[log.status] || STATUS_CONFIG.success;
                                return (
                                    <tr key={log.id} style={{ cursor:'pointer' }}
                                        onClick={() => setDetail(log)}>
                                        <td className="ps-4 fw-medium">{log.rule_name}</td>
                                        <td>
                                            <Badge bg="light" text="dark" style={{ fontSize:10 }}>
                                                {log.trigger_event}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={sc.bg} className="d-flex align-items-center gap-1"
                                                style={{ width:'fit-content' }}>
                                                {sc.icon}{sc.label}
                                            </Badge>
                                        </td>
                                        <td className="text-muted">{log.duration_ms}ms</td>
                                        <td className="text-danger small">
                                            {log.error_message
                                                ? log.error_message.slice(0, 50) + (log.error_message.length > 50 ? '…' : '')
                                                : '—'}
                                        </td>
                                        <td className="pe-4 text-muted">{fmtDate(log.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}

            {detail && <LogDetailModal logId={detail.id} onClose={() => setDetail(null)}/>}
        </>
    );
};

// ── Log Detail Modal ──────────────────────────────────────────────────────────
const LogDetailModal = ({ logId, onClose }) => {
    const [log, setLog] = useState(null);

    useEffect(() => {
        axiosInstance.get(`/workflows/logs/${logId}`)
            .then(r => setLog(r.data.data)).catch(()=>{});
    }, [logId]);

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">Execution Log Detail</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!log ? <div className="text-center py-3"><Spinner animation="border"/></div> : (
                    <>
                        <Row className="g-3 mb-4">
                            <Col xs={6}><strong>Rule:</strong> {log.rule_name}</Col>
                            <Col xs={6}><strong>Event:</strong> {log.trigger_event}</Col>
                            <Col xs={6}><strong>Status:</strong> <Badge bg={STATUS_CONFIG[log.status]?.bg||'secondary'}>{log.status}</Badge></Col>
                            <Col xs={6}><strong>Duration:</strong> {log.duration_ms}ms</Col>
                        </Row>

                        {log.error_message && (
                            <Alert variant="danger" className="small">{log.error_message}</Alert>
                        )}

                        <div className="fw-bold small mb-2">Actions Executed:</div>
                        {(log.actions_run || []).map((a, i) => (
                            <div key={i} className="p-2 rounded mb-2"
                                style={{ background: a.error ? '#fff1f2' : '#f0fdf4', fontSize:12 }}>
                                <span className="fw-medium">{i+1}. {a.type}</span>
                                {a.error
                                    ? <span className="text-danger ms-2">✗ {a.error}</span>
                                    : <span className="text-success ms-2">✓ {JSON.stringify(a.result)}</span>}
                            </div>
                        ))}

                        <div className="fw-bold small mt-3 mb-2">Trigger Payload:</div>
                        <pre className="small p-3 rounded" style={{ background:'#f8fafc', maxHeight:200, overflowY:'auto' }}>
                            {JSON.stringify(log.trigger_payload, null, 2)}
                        </pre>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default WorkflowPage;
